import type { Dielec, Varianta, Zostava } from "@/api/schemas";

/**
 * Rozdiel dvoch výpočtov tej istej zákazky.
 *
 * Je to **čistá funkcia bez dotazov** — `GET /api/zakazky/{id}` vracia
 * všetky varianty naraz s celou hierarchiou aj cenami, takže oba výpočty
 * už na obrazovke sú a porovnanie nepotrebuje endpoint.
 *
 * Páruje sa podľa **kódu**, nie podľa id: výrobná varianta vzniká
 * importom alebo duplikovaním, takže tie isté zostavy a dielce majú nové
 * id, ale rovnaký kód. Párovanie podľa id by ukázalo, že sa vymenilo
 * všetko.
 */

export type StavRozdielu = "len_v_a" | "len_v_b" | "zmenene" | "rovnake";

export interface SucetRozdiel {
  pole: string;
  /** Jednotka pre zobrazenie — € alebo %. */
  /** `koef` — koeficient ponuky (KOLO 231), „×1,08". */
  jednotka: "eur" | "pct" | "koef";
  a: number;
  b: number;
  rozdiel: number;
  /** Rozdiel v % voči A. `null` keď je A nula (delenie nulou). */
  rozdielPct: number | null;
}

export interface DielecRozdiel {
  kod: string;
  stav: StavRozdielu;
  /** Ktoré polia sa líšia — do popisu riadku. */
  zmenenePolia: string[];
  a?: Dielec;
  b?: Dielec;
  /** Kód na strane B, keď sa páry našli podľa názvu a ID sa líšia. */
  kodB?: string;
}

export interface ZostavaRozdiel {
  kod: string;
  nazov: string;
  stav: StavRozdielu;
  pocetKsA: number | null;
  pocetKsB: number | null;
  cenaA: number | null;
  cenaB: number | null;
  cenaRozdiel: number;
  dielce: DielecRozdiel[];
  /** Kód na strane B, keď sa páry našli podľa názvu a ID sa líšia. */
  kodB?: string;
}

export interface Porovnanie {
  sucty: SucetRozdiel[];
  zostavy: ZostavaRozdiel[];
  /** Zostavy bez akéhokoľvek rozdielu — v UI zbalené za jeden riadok. */
  rovnakychZostav: number;
}

/** Jeden pár položiek — jedna zo strán môže chýbať. */
export interface Par<T> {
  kod: string;
  a?: T;
  b?: T;
  /** Kód na strane B, keď sa pár našiel podľa názvu a kódy sa líšia. */
  kodB?: string;
}

/**
 * Spáruje položky **podľa kódu, zvyšky podľa názvu**.
 *
 * Kód je prvý a hlavný kľúč. Od KOLA 97 ho ale import generuje (`01`,
 * `01_p1`), takže dva výpočty tej istej zákazky môžu mať čísla posunuté;
 * a ručne robená varianta sa s importovanou nespárovala nikdy, lebo tá
 * mala v kóde meno uzla z TopSolidu.
 *
 * Zvyšky sa preto skúsia podľa názvu — ale **len keď je názov jedinečný
 * na oboch stranách zvyškov**. TopSolid pomenúva rovnaké dielce rovnako
 * (`bok_l` viackrát) a slepé párovanie podľa mena by vyrobilo nepravdivé
 * „zmenené".
 */
export function sparuj<T>(
  aItems: T[],
  bItems: T[],
  kluc: (x: T) => string,
  nazov: (x: T) => string,
): Par<T>[] {
  const mapaA = new Map<string, T>();
  const mapaB = new Map<string, T>();
  for (const x of aItems) if (kluc(x)) mapaA.set(kluc(x), x);
  for (const x of bItems) if (kluc(x)) mapaB.set(kluc(x), x);

  const pary: Par<T>[] = [];
  const zvyskyA: T[] = [];
  const zvyskyB: T[] = [];

  for (const [kod, a] of mapaA) {
    const b = mapaB.get(kod);
    if (b) pary.push({ kod, a, b });
    else zvyskyA.push(a);
  }
  for (const [kod, b] of mapaB) {
    if (!mapaA.has(kod)) zvyskyB.push(b);
  }

  const podlaNazvu = (items: T[]): Map<string, T | null> => {
    const m = new Map<string, T | null>();
    for (const x of items) {
      const n = nazov(x);
      if (!n) continue;
      // druhý výskyt toho istého názvu robí meno nepoužiteľným kľúčom
      m.set(n, m.has(n) ? null : x);
    }
    return m;
  };
  const nazvyB = podlaNazvu(zvyskyB);
  const nazvyA = podlaNazvu(zvyskyA);
  const spravovaneB = new Set<T>();

  for (const a of zvyskyA) {
    const n = nazov(a);
    const b = n ? nazvyB.get(n) : undefined;
    if (b && nazvyA.get(n) === a) {
      pary.push({ kod: kluc(a), a, b, kodB: kluc(b) });
      spravovaneB.add(b);
    } else {
      pary.push({ kod: kluc(a), a });
    }
  }
  for (const b of zvyskyB) {
    if (!spravovaneB.has(b)) pary.push({ kod: kluc(b), b });
  }

  return pary.sort((x, y) => x.kod.localeCompare(y.kod));
}

/** `"120.50"` → `120.5`; `null`/`undefined` → `0`. */
function cislo(v: unknown): number {
  if (v === null || v === undefined) return 0;
  const n = typeof v === "number" ? v : Number.parseFloat(String(v));
  return Number.isFinite(n) ? n : 0;
}

/**
 * Rovnosť dvoch hodnôt dielca.
 *
 * Číselné polia prídu z API ako stringy (`Decimal`), takže `"500"` a
 * `"500.00"` je tá istá hodnota a v rozdiele nemá čo robiť. Porovnáva sa
 * číselne **len keď sa obe strany dajú prečítať ako číslo** — inak by
 * `material_slot_id` skončilo ako `NaN === NaN` a dva rôzne sloty by
 * vyšli rovnako.
 */
function rovnakaHodnota(va: unknown, vb: unknown): boolean {
  if (va === vb) return true;
  if (va == null || vb == null) return (va ?? null) === (vb ?? null);
  const na = Number.parseFloat(String(va));
  const nb = Number.parseFloat(String(vb));
  if (Number.isFinite(na) && Number.isFinite(nb)) return na === nb;
  return String(va) === String(vb);
}

/** Polia dielca, ktorých zmena je pre porovnanie zaujímavá. */
const DIELEC_POLIA: { kluc: keyof Dielec; label: string }[] = [
  { kluc: "pocet_ks", label: "počet ks" },
  { kluc: "x_mm", label: "X" },
  { kluc: "y_mm", label: "Y" },
  { kluc: "z_mm", label: "Z" },
  { kluc: "material_slot_id", label: "materiál" },
  { kluc: "operacia", label: "výrobný postup" },
  // Bez tohto by sa dielec s dopísaným „lakovať" tváril ako zhodný
  // — je to jediné pole stĺpca Operácie, ktoré user mení (KOLO 108).
  { kluc: "operacie_popis", label: "operácie" },
  { kluc: "nazov_strojovy", label: "názov" },
];

/**
 * Porovná dielce jednej spárovanej zostavy.
 *
 * Materiál sa porovnáva cez `material_slot_id`, nie cez názov materiálu:
 * slot je to, čo user nastavuje, a jeho zmena je presne to, čo chce
 * v rozdiele vidieť.
 */
function porovnajDielce(
  aDielce: Dielec[],
  bDielce: Dielec[],
): DielecRozdiel[] {
  const out: DielecRozdiel[] = [];
  for (const { kod, a, b, kodB } of sparuj(
    aDielce,
    bDielce,
    (d) => d.kod ?? "",
    (d) => d.nazov_strojovy ?? "",
  )) {
    if (a && !b) {
      out.push({ kod, stav: "len_v_a", zmenenePolia: [], a });
      continue;
    }
    if (!a && b) {
      out.push({ kod, stav: "len_v_b", zmenenePolia: [], b });
      continue;
    }
    if (!a || !b) continue;

    const zmenene: string[] = [];
    for (const { kluc, label } of DIELEC_POLIA) {
      const va = (a as Record<string, unknown>)[kluc as string];
      const vb = (b as Record<string, unknown>)[kluc as string];
      if (!rovnakaHodnota(va, vb)) zmenene.push(label);
    }
    out.push({
      kod,
      stav: zmenene.length ? "zmenene" : "rovnake",
      zmenenePolia: zmenene,
      a,
      b,
      kodB,
    });
  }
  return out;
}

function sucetRozdiel(
  pole: string,
  jednotka: "eur" | "pct" | "koef",
  a: unknown,
  b: unknown,
): SucetRozdiel {
  const ca = cislo(a);
  const cb = cislo(b);
  const rozdiel = cb - ca;
  return {
    pole,
    jednotka,
    a: ca,
    b: cb,
    rozdiel,
    rozdielPct: ca === 0 ? null : (rozdiel / ca) * 100,
  };
}

export function porovnaj(a: Varianta, b: Varianta): Porovnanie {
  const sucty: SucetRozdiel[] = [
    sucetRozdiel("Bez DPH", "eur", a.cena_bez_dph, b.cena_bez_dph),
    sucetRozdiel("DPH", "eur", a.dph, b.dph),
    sucetRozdiel("S DPH", "eur", a.cena_s_dph, b.cena_s_dph),
    // Rozpočet a koeficient ponuky (KOLO 231) — cena ponuky je rozpočet
    // × koeficient pred zľavou
    sucetRozdiel("Rozpočet", "eur", a.rozpocet, b.rozpocet),
    sucetRozdiel("Koeficient", "koef", a.koef_ponuky ?? 1, b.koef_ponuky ?? 1),
    sucetRozdiel("Zisk", "pct", a.zisk_pct, b.zisk_pct),
    sucetRozdiel("Sleva", "pct", a.sleva_pct, b.sleva_pct),
  ];

  const zostavyA = (a.zostavy ?? []) as Zostava[];
  const zostavyB = (b.zostavy ?? []) as Zostava[];
  const zostavy: ZostavaRozdiel[] = [];
  let rovnakych = 0;

  for (const { kod, a: za, b: zb, kodB } of sparuj(
    zostavyA,
    zostavyB,
    (z) => z.kod ?? "",
    (z) => z.nazov ?? "",
  )) {
    const cenaA = za ? cislo(za.cena_celkom) : null;
    const cenaB = zb ? cislo(zb.cena_celkom) : null;
    const dielce =
      za && zb
        ? porovnajDielce(
            (za.dielce ?? []) as Dielec[],
            (zb.dielce ?? []) as Dielec[],
          )
        : [];

    let stav: StavRozdielu;
    if (za && !zb) stav = "len_v_a";
    else if (!za && zb) stav = "len_v_b";
    else {
      const zmenaCeny = (cenaA ?? 0) !== (cenaB ?? 0);
      const zmenaKs = za!.pocet_ks !== zb!.pocet_ks;
      const zmenaDielcov = dielce.some((d) => d.stav !== "rovnake");
      stav = zmenaCeny || zmenaKs || zmenaDielcov ? "zmenene" : "rovnake";
    }
    if (stav === "rovnake") rovnakych += 1;

    zostavy.push({
      kod,
      nazov: (zb ?? za)!.nazov,
      stav,
      pocetKsA: za?.pocet_ks ?? null,
      pocetKsB: zb?.pocet_ks ?? null,
      cenaA,
      cenaB,
      cenaRozdiel: (cenaB ?? 0) - (cenaA ?? 0),
      dielce,
      kodB,
    });
  }

  return { sucty, zostavy, rovnakychZostav: rovnakych };
}

/**
 * Default výber pre dialóg: A = aktívny výpočet, B = ten druhý najnovší.
 *
 * Keď má zákazka výrobnú variantu, vyjde ako B — najčastejšia otázka je
 * „čo sme naplánovali vs. čo sa reálne robí".
 */
export function defaultVyber(
  varianty: Varianta[],
  aktivnaId: string | null | undefined,
): { aId: string | null; bId: string | null } {
  if (varianty.length === 0) return { aId: null, bId: null };
  const aktivna =
    varianty.find((v) => v.id === aktivnaId) ??
    varianty.find((v) => !v.je_vyrobna) ??
    varianty[0]!;
  const ostatne = varianty.filter((v) => v.id !== aktivna.id);
  const vyrobna = ostatne.find((v) => v.je_vyrobna);
  const b = vyrobna ?? ostatne[ostatne.length - 1] ?? null;
  return { aId: aktivna.id, bId: b?.id ?? null };
}
